import { useState } from "react";
import { IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useThemeMediaQuery from "@fuse/hooks/useThemeMediaQuery";

interface RemoveRecordDialogProps {
	petName: string;
	chip: string;
	email: string;
	onCancel: () => void;
	onDone: () => void;
}

declare global {
	interface Window {
		_cio: any;
	}
}

function loadCio() {
	if (document.getElementById("cio-tracker")) return;

	const _cio = (window._cio = window._cio || []);
	const stub = (f: string) => (...args: any[]) => _cio.push([f, ...args]);
	for (const method of ["load", "identify", "sidentify", "track", "page"]) {
		_cio[method] = stub(method);
	}

	const t = document.createElement("script");
	t.async = true;
	t.id = "cio-tracker";
	t.setAttribute("data-site-id", process.env.VITE_CIO_SITE_ID || "");
	t.setAttribute("data-use-array-params", "true");
	t.setAttribute("data-auto-track-page", "false");
	t.src = "https://assets.customer.io/assets/track.js";
	document.head.appendChild(t);
}

function RemoveRecordDialog({ petName, chip, email, onCancel, onDone }: RemoveRecordDialogProps) {
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("sm"));
	const [step, setStep] = useState<"confirm" | "success">("confirm");

	function handleRemove() {
		if (email) {
			loadCio();
			window._cio.identify({ id: email, pet_record_removed: true });
		}
		setStep("success");
	}

	if (step === "confirm") {
		return (
			<div className="flex flex-col">
				{/* Logo */}
				<div className="flex justify-center" style={{ marginBottom: 22 }}>
					<img src="assets/img/checkout-logo.svg" alt="Peeva" style={{ width: 64 }} />
				</div>

				{/* Header */}
				<div className="flex items-center justify-between" style={{ marginBottom: 22 }}>
					<Typography sx={{ fontSize: 22, fontWeight: 800, color: "#6B7280", lineHeight: 1.2 }}>
						Confirm
					</Typography>
					<IconButton onClick={onCancel} size="small">
						<CloseIcon />
					</IconButton>
				</div>

				{/* Body */}
				<Typography sx={{ fontSize: 16, fontWeight: 700, color: "#6B7280", lineHeight: 1.4, marginBottom: "22px" }}>
					Are you sure you want to remove this record from our North American central microchip registry and pet recovery database.
				</Typography>

				{/* Pet Record Card */}
				<div style={{ border: "3px solid #B0B0B0", borderRadius: 16, padding: "36px 24px", marginBottom: 22 }}>
					<div className="flex justify-center">
						<div className="flex flex-col gap-2">
							<Typography sx={{ fontSize: 18, fontWeight: 800, color: "#6B7280", lineHeight: 1.4 }}>
								Pet Record:
							</Typography>
							<Typography sx={{ fontSize: 18, fontWeight: 500, color: "#6B7280", lineHeight: 1.4 }}>
								Pet: <Typography component="span" sx={{ fontSize: 18, fontWeight: 800, color: "#6B7280" }}>{petName}</Typography>
							</Typography>
							<Typography sx={{ fontSize: 18, fontWeight: 500, color: "#6B7280", lineHeight: 1.4 }}>
								Microchip ID: <Typography component="span" sx={{ fontSize: 18, fontWeight: 800, color: "#6B7280" }}>{chip}</Typography>
							</Typography>
						</div>
					</div>
				</div>

				{/* Buttons */}
				<div className="flex flex-col items-center" style={{ gap: 22 }}>
					<button
						onClick={handleRemove}
						style={{
							width: isMobile ? "100%" : undefined,
							minWidth: isMobile ? undefined : 220,
							padding: "12px 48px",
							border: "2px solid #52BFF4",
							color: "#52BFF4",
							background: "transparent",
							borderRadius: 9999,
							fontWeight: 700,
							fontSize: 16,
							cursor: "pointer",
						}}
					>
						Yes, Remove Record
					</button>
					<button
						onClick={onCancel}
						style={{
							width: isMobile ? "100%" : undefined,
							minWidth: isMobile ? undefined : 220,
							padding: "12px 48px",
							border: "none",
							background: "#FF9500",
							color: "#fff",
							borderRadius: 9999,
							fontWeight: 700,
							fontSize: 16,
							cursor: "pointer",
						}}
					>
						No, Keep Record
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-5">
			{/* Header */}
			<div className="flex items-center justify-between">
				<Typography variant="h5" fontWeight={700}>
					Record Removed
				</Typography>
				<IconButton onClick={onDone} size="small">
					<CloseIcon />
				</IconButton>
			</div>

			{/* Body */}
			<Typography variant="body1">
				This pet record has been removed from future activation messages.
			</Typography>

			{/* Close Button */}
			<button
				onClick={onDone}
				style={{
					border: "none",
					background: "#FF9500",
					color: "#fff",
					borderRadius: 9999,
					padding: "12px 24px",
					fontWeight: 700,
					fontSize: 16,
					cursor: "pointer",
				}}
			>
				Close
			</button>
		</div>
	);
}

export default RemoveRecordDialog;
